import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { User } from '../../store/authStore';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { api } from '../../services/api';

interface TopBarProps {
  user: User | null;
}

const ROLE_BADGE_CLASS: Record<string, string> = {
  root: 'badge-error',
  company_admin: 'badge-primary',
  agent: 'badge-info',
  end_user: 'badge-ghost',
};

const ROLE_LABEL: Record<string, string> = {
  root: 'Root',
  company_admin: 'Admin',
  agent: 'Agente',
  end_user: 'Usuario',
};

function getInitials(user: User | null): string {
  if (!user) return '?';
  const first = user.first_name?.[0] ?? '';
  const last = user.last_name?.[0] ?? '';
  return (first + last).toUpperCase() || '?';
}

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

// --- Notification types ---

interface NotificationPreview {
  id: number;
  type: 'contract_expiry' | 'visit_reminder' | 'new_message' | 'payment_due' | 'listing_expired' | 'system';
  title: string;
  body: string;
  is_read: boolean;
  created_at: string;
}

interface NotificationsPreviewResponse {
  data: NotificationPreview[];
}

interface UnreadCountResponse {
  data: { count: number };
}

const NOTIF_TYPE_ICON: Record<string, string> = {
  contract_expiry: '\uD83D\uDCC4',
  visit_reminder: '\uD83D\uDCC5',
  new_message: '\uD83D\uDCAC',
  payment_due: '\uD83D\uDCB3',
  listing_expired: '\uD83C\uDFE0',
  system: '\u2699\uFE0F',
};

function notifTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

export default function TopBar({ user }: TopBarProps): React.ReactElement {
  const { t } = useTranslation('common');
  const { logout } = useAuthStore();
  const { cycleSidebar, toggleMobileSidebar, theme, toggleTheme, locale, setLocale } = useUIStore();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const queryClient = useQueryClient();
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);

  // Fetch unread count (polls every 30s)
  const { data: unreadData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => api.get<UnreadCountResponse>('/v1/notifications/unread-count'),
    refetchInterval: 30000,
  });

  const unreadCount = unreadData?.data?.count ?? 0;

  // Fetch latest 5 notifications for dropdown
  const { data: previewData } = useQuery({
    queryKey: ['notifications-preview'],
    queryFn: () => api.get<NotificationsPreviewResponse>('/v1/notifications', { page_size: 5 }),
    enabled: bellOpen,
  });

  const previewNotifications = previewData?.data ?? [];

  // Mark single notification as read from dropdown
  const markReadMutation = useMutation({
    mutationFn: (id: number) => api.patch(`/v1/notifications/${id}/read`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-preview'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Close bell dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setBellOpen(false);
      }
    };
    if (bellOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [bellOpen]);

  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  const handleHamburgerClick = (): void => {
    if (isMobile) {
      toggleMobileSidebar();
    } else {
      cycleSidebar();
    }
  };

  const displayName = user ? `${user.first_name} ${user.last_name}` : '';
  const roleBadge = ROLE_BADGE_CLASS[user?.role ?? 'end_user'] ?? 'badge-ghost';
  const roleLabel = ROLE_LABEL[user?.role ?? 'end_user'] ?? user?.role ?? '';

  return (
    <div className="navbar bg-base-100 shadow-sm min-h-[64px] px-4 z-20">
      {/* Left: hamburger */}
      <div className="flex-none">
        <button
          className="btn btn-ghost btn-sm btn-square"
          onClick={handleHamburgerClick}
          aria-label="Toggle sidebar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-5 h-5 stroke-current">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Center: spacer */}
      <div className="flex-1" />

      {/* Right: notification bell + user dropdown */}
      <div className="flex-none flex items-center gap-1">
        {/* Notification Bell */}
        <div className="relative" ref={bellRef}>
          <button
            className="btn btn-ghost btn-sm btn-square"
            onClick={() => setBellOpen((prev) => !prev)}
            aria-label="Notificaciones"
          >
            <div className="indicator">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
              </svg>
              {unreadCount > 0 && (
                <span className="indicator-item badge badge-xs badge-error text-white font-bold">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </button>

          {/* Bell Dropdown */}
          {bellOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-base-100 rounded-box shadow-lg border border-base-200 z-[60]">
              <div className="px-4 py-3 border-b border-base-200">
                <h3 className="font-semibold text-sm">Notificaciones</h3>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {previewNotifications.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-base-content/50">
                    Sin notificaciones
                  </div>
                ) : (
                  previewNotifications.map((notif) => (
                    <button
                      key={notif.id}
                      className={`w-full text-left px-4 py-3 hover:bg-base-200 transition-colors flex items-start gap-3 border-b border-base-200/50 last:border-b-0 ${
                        !notif.is_read ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => {
                        if (!notif.is_read) markReadMutation.mutate(notif.id);
                        setBellOpen(false);
                        navigate('/app/notifications');
                      }}
                    >
                      <span className="text-lg flex-shrink-0">{NOTIF_TYPE_ICON[notif.type] || '\u2699\uFE0F'}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${!notif.is_read ? 'font-semibold' : 'text-base-content/70'}`}>
                          {notif.title}
                        </p>
                        <p className="text-xs text-base-content/50 truncate">{notif.body}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-xs text-base-content/40">{notifTimeAgo(notif.created_at)}</span>
                        {!notif.is_read && <span className="w-2 h-2 rounded-full bg-primary" />}
                      </div>
                    </button>
                  ))
                )}
              </div>
              <div className="px-4 py-2 border-t border-base-200">
                <button
                  className="btn btn-ghost btn-xs btn-block"
                  onClick={() => {
                    setBellOpen(false);
                    navigate('/app/notifications');
                  }}
                >
                  Ver todas
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="dropdown dropdown-end">
          {/* Dropdown trigger */}
          <div tabIndex={0} role="button" className="btn btn-ghost gap-2 px-2">
            {/* Avatar with initials */}
            <div className="avatar placeholder">
              <div className="bg-neutral text-neutral-content rounded-full w-8 h-8">
                <span className="text-xs font-bold">{getInitials(user)}</span>
              </div>
            </div>
            {/* Name and role (hidden on very small screens) */}
            <div className="hidden sm:flex flex-col items-start">
              <span className="text-sm font-medium leading-tight">{displayName}</span>
              <span className={`badge badge-xs ${roleBadge} leading-tight`}>{roleLabel}</span>
            </div>
            {/* Chevron */}
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 opacity-60">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </div>

          {/* Dropdown menu */}
          <ul tabIndex={0} className="dropdown-content z-[1] menu menu-sm p-2 shadow-lg bg-base-100 rounded-box w-56 mt-2 border border-base-200">
            {/* User info header */}
            <li className="menu-title px-3 py-2">
              <span className="text-sm font-semibold">{displayName}</span>
            </li>

            {/* Mi Perfil */}
            <li>
              <button onClick={() => navigate('/app/profile')} className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
                {t('nav.profile')}
              </button>
            </li>

            {/* Idioma toggle */}
            <li>
              <button onClick={() => setLocale(locale === 'es' ? 'en' : 'es')} className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
                </svg>
                {locale === 'es' ? 'English' : 'Espa\u00f1ol'}
              </button>
            </li>

            {/* Theme toggle */}
            <li>
              <button onClick={toggleTheme} className="flex items-center gap-2">
                {theme === 'light' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                  </svg>
                )}
                {theme === 'light' ? 'Modo oscuro' : 'Modo claro'}
              </button>
            </li>

            {/* Divider */}
            <div className="divider my-1" />

            {/* Logout */}
            <li>
              <button onClick={handleLogout} className="flex items-center gap-2 text-error">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3-3h-9m9 0l-3-3m3 3l-3 3" />
                </svg>
                {t('buttons.logout')}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
